/**
 * Service Request Form Component
 * 서비스 요청 작성 폼
 */

import {
  AddressAutocomplete,
  type AddressDetails,
} from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Send } from "lucide-react";
import { useState } from "react";

interface ServiceRequestFormProps {
  category: "plumbing" | "electrical" | "cleaning" | "moving" | "food";
  onSuccess?: () => void;
}

export function ServiceRequestForm({
  category,
  onSuccess,
}: ServiceRequestFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postcode, setPostcode] = useState("");
  const [latitude, setLatitude] = useState<number | undefined>();
  const [longitude, setLongitude] = useState<number | undefined>();
  const [preferredDate, setPreferredDate] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim() || !description.trim() || !address.trim()) {
        throw new Error("제목, 설명, 주소를 입력해주세요");
      }

      const body: any = {
        category,
        title: title.trim(),
        description: description.trim(),
        address: address.trim(),
        city: city || undefined,
        state: state || undefined,
        postcode: postcode || undefined,
        latitude,
        longitude,
        preferred_date: preferredDate || undefined,
        budget_min: budgetMin
          ? Math.round(parseFloat(budgetMin) * 100)
          : undefined,
        budget_max: budgetMax
          ? Math.round(parseFloat(budgetMax) * 100)
          : undefined,
      };

      const response = await apiRequest("POST", "/api/service-requests", body);
      return response as any;
    },
    onSuccess: (data: any) => {
      toast({
        title: "요청이 등록되었습니다",
        description: `${
          data.matched_businesses?.length || 0
        }개 업체에 요청이 전달되었습니다`,
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "요청 등록 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  const handleAddressChange = (value: string, details?: AddressDetails) => {
    setAddress(value);
    if (details) {
      setCity(details.city || "");
      setState(details.state || "");
      setPostcode(details.postcode || "");
      if ((details as any).lat && (details as any).lng) {
        setLatitude((details as any).lat);
        setLongitude((details as any).lng);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>제목 *</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="예: 싱크대 배관 누수 수리"
          required
        />
      </div>

      <div className="space-y-2">
        <Label>상세 설명 *</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
          placeholder="서비스 요청에 대한 자세한 설명을 입력해주세요"
          required
        />
      </div>

      <div className="space-y-2">
        <Label>주소 *</Label>
        <AddressAutocomplete
          value={address}
          onChange={handleAddressChange}
          placeholder="서비스가 필요한 주소를 입력하세요"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>희망 날짜</Label>
          <Input
            type="date"
            value={preferredDate}
            onChange={(e) => setPreferredDate(e.target.value)}
            min={new Date().toISOString().split("T")[0]}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>예산 최소 (AUD)</Label>
          <Input
            type="number"
            value={budgetMin}
            onChange={(e) => setBudgetMin(e.target.value)}
            placeholder="예: 200"
            min="0"
          />
        </div>
        <div className="space-y-2">
          <Label>예산 최대 (AUD)</Label>
          <Input
            type="number"
            value={budgetMax}
            onChange={(e) => setBudgetMax(e.target.value)}
            placeholder="예: 500"
            min="0"
          />
        </div>
      </div>

      <Button
        className="w-full"
        onClick={() => submitMutation.mutate()}
        disabled={
          submitMutation.isPending ||
          !title.trim() ||
          !description.trim() ||
          !address.trim()
        }
      >
        {submitMutation.isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            등록 중...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            요청 등록하기
          </>
        )}
      </Button>
    </div>
  );
}
